// Reports — user-submitted abuse / safety / quality flags (T2 trust + safety).
//
// Lightweight signal channel that sits below the dispute mechanism. Reports
// against a venue, artist, message, or review fan into the admin moderation
// queue; admins triage them and may escalate, action, or dismiss.
//
// Two schemas:
//   - createReportInputSchema — user-submitted, called from POST /api/reports
//   - resolveReportInputSchema — admin-only, called from POST /api/admin/reports/:id/resolve
//
// Subject shape mirrors packages/db/src/schema/reports.ts. subject_id is a
// soft pointer (no FK) because subject_type spans multiple tables; the route
// validates existence at write time.
import { z } from 'zod';

import { idSchema, richTextSchema } from './common.js';

export const reportSubjectTypeSchema = z.enum([
  'venue',
  'artist',
  'message',
  'review',
  'exchange',
]);
export type ReportSubjectType = z.infer<typeof reportSubjectTypeSchema>;

export const reportCategorySchema = z.enum([
  'safety',
  'harassment',
  'spam_or_fake',
  'misrepresentation',
  'inappropriate_content',
  'other',
]);
export type ReportCategory = z.infer<typeof reportCategorySchema>;

export const reportStatusSchema = z.enum(['open', 'reviewing', 'actioned', 'dismissed']);
export type ReportStatus = z.infer<typeof reportStatusSchema>;

export const createReportInputSchema = z.object({
  subject_type: reportSubjectTypeSchema,
  subject_id: idSchema,
  category: reportCategorySchema,
  body: richTextSchema(2000).refine((s) => s.trim().length >= 20, {
    message: 'Tell us what happened — at least a sentence so we can triage.',
  }),
});
export type CreateReportInput = z.infer<typeof createReportInputSchema>;

export const resolveReportInputSchema = z.object({
  status: z.enum(['actioned', 'dismissed']),
  resolution_note: richTextSchema(2000).optional(),
  /**
   * One-click safety action: suspend the reported party pending review in the
   * same move as closing the report. Decision taken with the client — serious
   * safety / harassment reports lead to an immediate suspend-pending-review,
   * not an auto-suspend on submission. The admin opts in here; the route maps
   * the report's subject to a user (venue→owner, artist→profile user,
   * message→author, review→reviewer, exchange→the reported party) and drives
   * the User state machine's admin.suspend.
   */
  suspend_subject: z.boolean().optional().default(false),
  /** Reason recorded on the suspension + shared with the suspended member. */
  suspend_reason: richTextSchema(2000).optional(),
});
export type ResolveReportInput = z.infer<typeof resolveReportInputSchema>;

/** Categories that trip the "serious" treatment in the admin queue + UI. */
export const SERIOUS_REPORT_CATEGORIES = ['safety', 'harassment'] as const;

/** True when a report's category is a serious safety / harassment flag. */
export function isSeriousReportCategory(category: ReportCategory): boolean {
  return (SERIOUS_REPORT_CATEGORIES as readonly string[]).includes(category);
}
